import React, { type RefObject } from 'react';
import { motion, useReducedMotion } from 'framer-motion';
import { statementLines } from '../data/statement';
import { focusInView } from '../utils/reveal';
import { ServicesAccordion } from './ServicesAccordion';
import { useIsMobile } from '../hooks/useIsMobile';

/**
 * Huge left-aligned statement after the manifesto.
 * Proportions from the reference at 1280px: ~62px type, line height ~1, tight tracking, ~17px left gutter.
 * Each line comes into focus on scroll, one after the other, like the hero text.
 */
interface StatementSectionProps {
  /** The statement — once it has scrolled well up, the backdrop turns blue for the work */
  headingRef?: RefObject<HTMLHeadingElement>;
}
export function StatementSection({
  headingRef
}: StatementSectionProps) {
  const reduce = useReducedMotion();
  const mobile = useIsMobile();
  return (
    // Phones: more room before the first project (pb-32); desktop unchanged
    <section id="services" className="relative w-full scroll-mt-0 px-3 pb-32 pt-[clamp(15rem,calc(30*var(--vw)),33rem)] md:pb-[clamp(5rem,calc(9*var(--vw)),10rem)]" data-id="element-290" data-mp="21qoqd33d1pvi">
      <h2 ref={headingRef} data-ink="" className="max-w-[16ch] text-[clamp(2.5rem,calc(4.85*var(--vw)),6.5rem)] font-semibold leading-[1.02] tracking-[-0.045em] text-black md:max-w-none" data-id="element-291" data-mp="26k70u4pfqbck">
        {statementLines.map((line, i) => <motion.span className="block pb-[0.04em]"
        // Desktop: waits until the line is a good way up the screen (25% above the bottom edge).
        // Phones: each line wraps onto 2–3 rows, so it resolves sooner (12% up), with a softer, longer settle
        key={mobile ? `${line}-m` : line} {...mobile ? focusInView(i * 0.18, reduce, {
          y: 18,
          blur: 12,
          duration: 1.3,
          amount: 0.35,
          margin: '0px 0px -12% 0px'
        }) : focusInView(i * 0.2, reduce, {
          y: 14,
          blur: 14,
          amount: 0.5,
          margin: '0px 0px -25% 0px'
        })} data-id="element-292" data-mp="2kiwwdfwlw2yt" data-mpi={i}>
            {line}
          </motion.span>)}
      </h2>

      {/* Services: phones — on the left, flush with the statement; desktop — set off to the right */}
      <div className="mt-14 flex justify-start md:mt-[clamp(6rem,calc(10*var(--vw)),11rem)] md:justify-end" data-id="element-293" data-mp="31t0uck5tzdch">
        <ServicesAccordion data-id="element-294" data-mp="2wzfsgh149z80" />
      </div>
    </section>
  );
}